'use client';

import { c as _c } from "react/compiler-runtime";
import { motion, useInView } from 'framer-motion';
import { StaticImageData } from 'next/image';
import { ReactNode, useRef } from 'react';
import AnchorLink from '@/components/AnchorLink';
import OptimizedImage from '@/components/OptimizedImage';
import ViverseButton, { ViverseButtonVariants } from '@/components/ViverseButton';
import { cn } from '@/lib/utils';
export type Info = {
  title: string;
  background: string;
  backgroundImage: {
    image: StaticImageData;
    className?: string;
  };
  backgroundCoverImage?: StaticImageData;
  blurClassName?: string;
  description: string;
  ctas: {
    title: string;
    href: string;
    variant: ViverseButtonVariants;
    icon?: ReactNode;
  }[];
};
type InfoCardProps = {
  info: Info;
  className?: string;
  descriptionClassName?: string;
};
function InfoCard(t0) {
  const $ = _c(45);
  const {
    info,
    className,
    descriptionClassName
  } = t0;
  const ref = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      once: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isInView = useInView(ref, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = isInView ? 1 : 0;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      opacity: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== info.background) {
    t5 = {
      background: info.background
    };
    $[4] = info.background;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== info.blurClassName) {
    t6 = cn("absolute inset-1/4", info.blurClassName);
    $[6] = info.blurClassName;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <div className={t6} />;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== info.backgroundCoverImage) {
    t8 = info.backgroundCoverImage && <OptimizedImage src={info.backgroundCoverImage} className="absolute rounded-3xl object-cover" fill={true} sizes="(max-width: 1024px) 90vw, 50vw" alt="" />;
    $[10] = info.backgroundCoverImage;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== className) {
    t9 = cn("3xl:p-12 relative flex size-full overflow-hidden rounded-3xl px-6 py-5 md:p-8", className);
    $[12] = className;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = info.backgroundImage.image;
  let t11;
  if ($[14] !== info.backgroundImage.className) {
    t11 = cn("1.5xl:max-w-[80%]! absolute inset-[inherit]! w-full object-cover md:max-w-[50%]! lg:max-w-[45%]!", info.backgroundImage.className);
    $[14] = info.backgroundImage.className;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== info.backgroundImage.image || $[17] !== info.title || $[18] !== t11) {
    t12 = <OptimizedImage src={t10} className={t11} alt={info.title} fill={true} sizes="(max-width: 1024px) 80vw, 45vw" />;
    $[16] = info.backgroundImage.image;
    $[17] = info.title;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== info.title) {
    t13 = <p className="header-4 1.5xl:max-w-[198px] 3xl:max-w-[300px] leading-8">{info.title}</p>;
    $[20] = info.title;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== descriptionClassName) {
    t14 = cn("body-1 1.5xl:max-w-[198px] 3xl:max-w-[300px]", descriptionClassName);
    $[22] = descriptionClassName;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== info.description || $[25] !== t14) {
    t15 = <p className={t14}>{info.description}</p>;
    $[24] = info.description;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== info.ctas) {
    t16 = info.ctas.map(_temp);
    $[27] = info.ctas;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t16) {
    t17 = <div className="flex flex-wrap gap-4">{t16}</div>;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t13 || $[32] !== t15 || $[33] !== t17) {
    t18 = <div className="z-interactive flex flex-col gap-4 pb-[148px] md:max-w-[396px] md:pb-0">{t13}{t15}{t17}</div>;
    $[31] = t13;
    $[32] = t15;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t12 || $[36] !== t18 || $[37] !== t9) {
    t19 = <div className={t9}>{t12}{t18}</div>;
    $[35] = t12;
    $[36] = t18;
    $[37] = t9;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t19 || $[40] !== t4 || $[41] !== t5 || $[42] !== t7 || $[43] !== t8) {
    t20 = <motion.div ref={ref} className="relative flex flex-1 rounded-3xl" initial={t2} animate={t4} style={t5}>{t7}{t8}{t19}</motion.div>;
    $[39] = t19;
    $[40] = t4;
    $[41] = t5;
    $[42] = t7;
    $[43] = t8;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  return t20;
}
function _temp(cta, index) {
  return <AnchorLink href={cta.href} key={index}><ViverseButton className="px-4 text-sm leading-[22px]" variant={cta.variant}>{cta.icon}{cta.title}</ViverseButton></AnchorLink>;
}
export default InfoCard;